/**
 * Manage MVPs — Score Method → registration → filters → MVP cards.
 * Subgroup builder only when CMS requires subgroups (or subgroups already exist).
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { keepPreviousSameEntity, qk } from '@/lib/query-keys';
import { usePerformanceSummaries } from '@/hooks/queries/useMipsQueries';
import { useNpiProfiles, useSubgroups, type Subgroup } from '@/hooks/queries/useMvpQueries';
import { SubgroupBuilder } from './SubgroupBuilder';
import { SubgroupMvpAssignSection } from './SubgroupMvpAssignSection';
import { PopHealthCard } from './PopHealthCard';
import { MvpDetailDialog } from './MvpDetailDialog';
import { MipsDataService, type MvpDefinition } from '@/lib/mips-data-service';
import { isQcdrMeasureId } from '@/lib/mvp/quality';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { AlertTriangle, Check, Download, Info, Star, Users } from 'lucide-react';

interface ExploreMvpsTabProps {
  practiceId: string | number | null;
  programYear: number;
  /** Switch Configuration to the QPP Reports tab (unmatched-clinician banner CTA) */
  onGoToQppReports?: () => void;
}

interface MvpSelection {
  id: number; mvp_id: string; entity_type: string; subgroup_id: number | null;
  subgroup_name?: string | null; registered: boolean;
}

type MvpFilters = {
  search: string;
  specialty: string;
  clinicalTopic: string;
  cahpsOnly: boolean;
  selectedOnly: boolean;
};

const EMPTY_FILTERS: MvpFilters = {
  search: '',
  specialty: 'all',
  clinicalTopic: 'all',
  cahpsOnly: false,
  selectedOnly: false,
};

function useMvpSelections(practiceId: string | number | null, year: number) {
  return useQuery<MvpSelection[]>({
    queryKey: ['mvp', 'selections', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/selections?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<MvpSelection[]>(2, String(practiceId ?? '')),
  });
}

function usePracticeSettingsLite(practiceId: string | number | null, year: number) {
  const id = practiceId != null ? String(practiceId) : '';
  return useQuery<any>({
    queryKey: qk.practiceSettings(id || 'none', year),
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/settings?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, id),
  });
}

export function ExploreMvpsTab({ practiceId, programYear, onGoToQppReports }: ExploreMvpsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dataService = MipsDataService.getInstance();

  const [filters, setFilters] = useState<MvpFilters>(EMPTY_FILTERS);
  const [detailMvp, setDetailMvp] = useState<MvpDefinition | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [registeredBusyId, setRegisteredBusyId] = useState<number | null>(null);
  const [editSubgroupRequest, setEditSubgroupRequest] = useState<Subgroup | null>(null);
  const subgroupExportAllRef = useRef<(() => void) | null>(null);

  const mvps = useMemo(() => dataService.getMvps(programYear), [dataService, programYear]);
  const selectionsQ = useMvpSelections(practiceId, programYear);
  const summariesQ = usePerformanceSummaries(practiceId, programYear, 'GROUP');
  const settingsQ = usePracticeSettingsLite(practiceId, programYear);
  const profilesQ = useNpiProfiles(practiceId, programYear);
  const subgroupsQ = useSubgroups(practiceId, programYear);

  const measuresWithData = useMemo(() => {
    const set = new Set<string>();
    for (const s of summariesQ.data ?? []) {
      const id = s.measureId ?? s.measure_id;
      const hasCounts = s.counts && (Number(s.counts.den) + Number(s.counts.met) + Number(s.counts.not) + Number(s.counts.exc)) > 0;
      const hasStrata = Array.isArray(s.strata) && s.strata.some((st: any) => (Number(st.den) + Number(st.met) + Number(st.not) + Number(st.exc)) > 0);
      if (id && (hasCounts || hasStrata)) set.add(String(id));
    }
    return set;
  }, [summariesQ.data]);

  const specialties = useMemo(() => {
    const set = new Set<string>();
    mvps.forEach(m => m.specialtiesMostApplicableTo.forEach(s => set.add(s)));
    return [...set].sort();
  }, [mvps]);

  const clinicalTopics = useMemo(() => {
    const set = new Set<string>();
    mvps.forEach(m => { if (m.clinicalTopic) set.add(m.clinicalTopic); });
    return [...set].sort();
  }, [mvps]);

  const selections = selectionsQ.data ?? [];
  const groupSelection = selections.find(s => s.entity_type === 'group') ?? null;
  const selectionsByMvp = useMemo(() => {
    const map = new Map<string, MvpSelection[]>();
    for (const s of selections) {
      const list = map.get(s.mvp_id) ?? [];
      list.push(s);
      map.set(s.mvp_id, list);
    }
    return map;
  }, [selections]);

  const isSmallPractice = settingsQ.data?.is_small_practice === true;
  const multiSpecialty = profilesQ.data?.multiSpecialty ?? false;
  const specialtyCount = profilesQ.data?.specialtyCodes?.length ?? 0;
  const unmatchedNpis = profilesQ.data?.unmatchedNpis ?? 0;
  const matchedClinicians = profilesQ.data?.countedNpis
    ?? (profilesQ.data?.profiles ?? []).filter(p => (p as any).counts_for_practice).length;
  const subgroupRequired = multiSpecialty && !isSmallPractice;
  const subgroups = subgroupsQ.data ?? [];
  const showSubgroupTools = subgroupRequired || subgroups.length > 0;
  const anySelectedUnregistered = selections.some(s => !s.registered);

  const coveredCount = (mvp: MvpDefinition) => {
    const supported = mvp.qualityMeasureIds.filter(id => !isQcdrMeasureId(id));
    return supported.filter(id => measuresWithData.has(id)).length;
  };

  const visible = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const rows = mvps.filter(m => {
      if (filters.specialty !== 'all' && !m.specialtiesMostApplicableTo.includes(filters.specialty)) {
        return false;
      }
      if (filters.clinicalTopic !== 'all' && m.clinicalTopic !== filters.clinicalTopic) {
        return false;
      }
      if (filters.cahpsOnly && !m.hasCahps) return false;
      if (filters.selectedOnly && !selectionsByMvp.has(m.mvpId)) return false;
      if (q) {
        const hay = [
          m.mvpId, m.title, m.clinicalTopic, m.description,
          ...m.specialtiesMostApplicableTo,
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return [...rows].sort((a, b) => {
      const aSel = selectionsByMvp.has(a.mvpId) ? 1 : 0;
      const bSel = selectionsByMvp.has(b.mvpId) ? 1 : 0;
      if (aSel !== bSel) return bSel - aSel;
      const aReg = (selectionsByMvp.get(a.mvpId) ?? []).some(s => s.registered) ? 1 : 0;
      const bReg = (selectionsByMvp.get(b.mvpId) ?? []).some(s => s.registered) ? 1 : 0;
      if (aReg !== bReg) return bReg - aReg;
      return coveredCount(b) - coveredCount(a);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mvps, filters, selectionsByMvp, measuresWithData]);

  const filtersActive =
    filters.search.trim() !== ''
    || filters.specialty !== 'all'
    || filters.clinicalTopic !== 'all'
    || filters.cahpsOnly
    || filters.selectedOnly;

  const commitMvp = async (mvpId: string) => {
    if (!practiceId) return;
    setSaving(mvpId);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/selections?year=${programYear}`, {
        mvp_id: mvpId,
        entity_type: 'group',
        registered: false,
      });
      await queryClient.invalidateQueries({ queryKey: ['mvp', 'selections', practiceId, programYear] });
      toast({
        title: 'MVP selected',
        description: `${mvpId} saved as group intent. Mark Registered on the card after you register on CMS.`,
      });
    } catch (e: any) {
      toast({ title: 'Failed to save selection', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const clearSelection = async () => {
    if (!practiceId || !groupSelection) return;
    setSaving(groupSelection.mvp_id);
    try {
      await apiClient.delete(`/api/mvp/${practiceId}/selections/${groupSelection.id}?year=${programYear}`);
      await queryClient.invalidateQueries({ queryKey: ['mvp', 'selections', practiceId, programYear] });
      toast({ title: 'MVP selection cleared' });
    } catch (e: any) {
      toast({ title: 'Failed to clear selection', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const setRegistered = async (selection: MvpSelection, registered: boolean) => {
    if (!practiceId) return;
    setRegisteredBusyId(selection.id);
    try {
      await apiClient.patch(`/api/mvp/${practiceId}/selections/${selection.id}?year=${programYear}`, { registered });
      await queryClient.invalidateQueries({ queryKey: ['mvp', 'selections', practiceId, programYear] });
      const label = selection.entity_type === 'subgroup'
        ? (selection.subgroup_name || 'Subgroup')
        : 'Group';
      toast({
        title: registered ? `Registered — ${selection.mvp_id}` : `Registration cleared — ${selection.mvp_id}`,
        description: label,
      });
    } catch (e: any) {
      toast({ title: 'Failed to update registration', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setRegisteredBusyId(null);
    }
  };

  if (!practiceId) {
    return <Alert><Info className="h-4 w-4" /><AlertDescription>Select a practice first.</AlertDescription></Alert>;
  }
  if (mvps.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          No MVP catalog for PY{programYear} — MVPs start in PY2026. Switch the viewing year to explore them.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Alert className="border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700">
          {selections.length === 0
            ? 'CMS registration required for any MVP you report'
            : anySelectedUnregistered
              ? 'Confirm CMS registration on each selected MVP'
              : 'Selected MVPs marked registered'}
        </AlertTitle>
        <AlertDescription className="text-amber-600">
          Setting an MVP here tracks intent only — it does not register with CMS. Window:{' '}
          <strong className="text-amber-800">Apr 1 – Nov 30, {programYear}</strong>
          {' '}(CAHPS by Jun 30). After you register on the QPP website, check{' '}
          <strong className="text-amber-800">Registered with CMS</strong> on that MVP’s card
          {subgroupRequired ? ' (per subgroup assignment)' : ''}.
          {subgroups.length > 0 && (
            <> · {subgroups.length} subgroup{subgroups.length === 1 ? '' : 's'}</>
          )}
          {profilesQ.data?.lastHydrated && (
            <> · CMS roster {new Date(profilesQ.data.lastHydrated).toLocaleDateString()}</>
          )}
        </AlertDescription>
      </Alert>

      {unmatchedNpis > 0 && (
        <Alert className="border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700">
            {unmatchedNpis} clinician{unmatchedNpis === 1 ? '' : 's'} from claims couldn’t be matched
          </AlertTitle>
          <AlertDescription className="text-amber-600">
            These NPIs don’t count toward subgroup rules or Medicare charges until you confirm
            their CMS organization under{' '}
            {onGoToQppReports ? (
              <button
                type="button"
                className="font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-950"
                onClick={onGoToQppReports}
              >
                QPP Reports
              </button>
            ) : (
              <strong className="text-amber-800">QPP Reports</strong>
            )}
            . This banner clears when all claims clinicians are matched or excluded.
          </AlertDescription>
        </Alert>
      )}

      <PopHealthCard practiceId={practiceId} programYear={programYear} />

      <Card className="border-slate-200/80 bg-slate-50/70 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Search by suggested specialty, topic, or keyword. You can select any MVP.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground">Search
              <Input
                className="bg-white"
                placeholder="MVP ID, title, specialty…"
                value={filters.search}
                onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Label>
            <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground">Suggested specialty
              <Select
                value={filters.specialty}
                onValueChange={v => setFilters(prev => ({ ...prev, specialty: v }))}
              >
                <SelectTrigger className="bg-white"><SelectValue placeholder="Any suggested specialty" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any suggested specialty</SelectItem>
                  {specialties.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Label>
            <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground">Clinical topic
              <Select
                value={filters.clinicalTopic}
                onValueChange={v => setFilters(prev => ({ ...prev, clinicalTopic: v }))}
              >
                <SelectTrigger className="bg-white"><SelectValue placeholder="Any topic" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any topic</SelectItem>
                  {clinicalTopics.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Label>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
              <Checkbox
                checked={filters.cahpsOnly}
                onCheckedChange={v => setFilters(prev => ({ ...prev, cahpsOnly: v === true }))}
              />
              Includes CAHPS
            </Label>
            <Label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
              <Checkbox
                checked={filters.selectedOnly}
                onCheckedChange={v => setFilters(prev => ({ ...prev, selectedOnly: v === true }))}
              />
              Selected only
            </Label>
            {filtersActive && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-muted-foreground"
                onClick={() => setFilters(EMPTY_FILTERS)}
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* One quiet status line — not a second alert stack */}
          <p className={`text-xs leading-relaxed pt-1 border-t border-slate-200/80 ${
            subgroupRequired ? 'text-amber-800' : 'text-muted-foreground'
          }`}>
            {subgroupRequired ? (
              <>
                <Users className="inline h-3.5 w-3.5 mr-1 -mt-0.5 text-amber-600" />
                <span className="font-medium">Subgroups required</span>
                {' '}· {specialtyCount} specialties · not a small practice
              </>
            ) : (
              <>
                <Users className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                <span className="font-medium text-foreground/80">Group reporting</span>
                {' '}· subgroups not required
                {matchedClinicians > 0 && (
                  <> · {matchedClinicians} matched clinician{matchedClinicians === 1 ? '' : 's'}</>
                )}
                {specialtyCount > 0 && (
                  <> · {specialtyCount} specialt{specialtyCount === 1 ? 'y' : 'ies'}</>
                )}
              </>
            )}
          </p>
        </CardContent>
      </Card>

      {showSubgroupTools && practiceId && (
        <SubgroupBuilder
          practiceId={practiceId}
          programYear={programYear}
          isSmallPractice={isSmallPractice}
          selections={selections}
          hideStatusAlerts
          editRequest={editSubgroupRequest}
          onEditRequestHandled={() => setEditSubgroupRequest(null)}
          headerActions={subgroups.length > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => subgroupExportAllRef.current?.()}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Export CSV
            </Button>
          ) : null}
        >
          {subgroups.length > 0 ? (
            <SubgroupMvpAssignSection
              practiceId={practiceId}
              programYear={programYear}
              selections={selections}
              onManageMembers={sg => setEditSubgroupRequest(sg)}
              embedded
              exportAllRef={subgroupExportAllRef}
            />
          ) : null}
        </SubgroupBuilder>
      )}

      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex flex-wrap items-end justify-between gap-3 pt-1">
          <div>
            <h3 className="text-sm font-semibold text-foreground tracking-wide">MVPs</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {visible.length} of {mvps.length}
              {filtersActive ? ' matching filters' : ''}
            </p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map(mvp => {
            const mvpSelections = selectionsByMvp.get(mvp.mvpId) ?? [];
            const isGroupCommitted = groupSelection?.mvp_id === mvp.mvpId;
            const isSelected = mvpSelections.length > 0;
            const anyRegistered = mvpSelections.some(s => s.registered);
            return (
              <Card
                key={mvp.mvpId}
                className={`flex flex-col ${isSelected ? 'border-primary shadow-sm' : ''}`}
              >
                <CardHeader className="pb-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-base font-semibold leading-snug">
                      {mvp.title}
                    </CardTitle>
                    <Badge className="shrink-0 bg-slate-800 text-white hover:bg-slate-800 font-mono text-xs tracking-wide px-2.5 py-0.5">
                      {mvp.mvpId}
                    </Badge>
                  </div>
                  <CardDescription className="space-y-1.5 text-sm">
                    {mvp.clinicalTopic && (
                      <span className="block text-xs font-medium text-muted-foreground/90 tracking-wide">
                        {mvp.clinicalTopic}
                      </span>
                    )}
                    {mvp.description ? (
                      <span className="block leading-relaxed line-clamp-3 text-foreground/80">
                        {mvp.description}
                      </span>
                    ) : (
                      <span className="block text-muted-foreground/70 italic">No description</span>
                    )}
                  </CardDescription>
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {isGroupCommitted && (
                      <Badge variant="default" className="font-normal">
                        <Star className="h-3 w-3 mr-1" />Our MVP
                      </Badge>
                    )}
                    {!isGroupCommitted && mvpSelections.some(s => s.entity_type === 'subgroup') && (
                      <Badge variant="secondary" className="font-normal">Subgroup</Badge>
                    )}
                    {anyRegistered && (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-normal">
                        Registered
                      </Badge>
                    )}
                    {mvp.hasCahps && (
                      <Badge variant="outline" className="font-normal text-[10px]">CAHPS</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0 mt-auto space-y-3">
                  {mvpSelections.length > 0 && (
                    <div className="space-y-1.5 rounded-md border border-emerald-200/80 bg-emerald-50/40 p-2.5">
                      {mvpSelections.map(sel => {
                        const entityLabel = sel.entity_type === 'subgroup'
                          ? (sel.subgroup_name || 'Subgroup')
                          : 'Group';
                        const inputId = `reg-${sel.id}`;
                        return (
                          <div key={sel.id} className="flex items-center gap-2">
                            <Checkbox
                              id={inputId}
                              checked={!!sel.registered}
                              disabled={registeredBusyId === sel.id}
                              onCheckedChange={v => setRegistered(sel, v === true)}
                            />
                            <Label htmlFor={inputId} className="text-xs text-emerald-900 cursor-pointer leading-snug">
                              Registered with CMS
                              <span className="text-emerald-700/80 font-normal"> · {entityLabel}</span>
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {isGroupCommitted ? (
                      <Button size="sm" variant="outline" disabled={saving === mvp.mvpId} onClick={clearSelection}>
                        Clear
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant={subgroupRequired ? 'outline' : 'default'}
                        className={subgroupRequired ? 'text-muted-foreground' : ''}
                        disabled={saving === mvp.mvpId}
                        title={subgroupRequired ? 'CMS expects subgroup MVPs when multispecialty' : undefined}
                        onClick={() => commitMvp(mvp.mvpId)}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        {subgroupRequired ? 'Set group intent' : 'Set as our MVP'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setDetailMvp(mvp)}>
                      View detail
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {visible.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No MVPs match these filters.
            {filtersActive && (
              <Button type="button" variant="link" className="px-1 h-auto" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            )}
          </p>
        )}
      </div>

      <MvpDetailDialog
        mvp={detailMvp}
        programYear={programYear}
        open={!!detailMvp}
        onOpenChange={(open) => { if (!open) setDetailMvp(null); }}
      />
    </div>
  );
}
